/**
 * /pricing/specialty — one-page buy surface for the 4 new SKUs from
 * the 2026-02 audit (Trainer Profile, Pet Parent Pack, Show Resume PDF,
 * API Partner). Keeps the main /pricing page focused on the core ladder
 * while still giving each new SKU a discoverable home.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { HeartIcon as Heart, TrophyIcon as Trophy, ScrollIcon as Scroll, CodeIcon as Code } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { ReaderAppBanner } from "@/components/ReaderAppBanner";

const SPECIALTY_SKUS = [
  {
    package_id: "trainer_profile_monthly",
    name: "Trainer Profile",
    price: "$9 / mo",
    annual_id: "trainer_profile_annual",
    annual_price: "$89 / yr",
    icon: Trophy,
    accent: "gold",
    persona: "Trainers (no LMS needed)",
    desc: "Public profile in /trainer-directory with Verified Trainer badge. For trainers who want discoverability without running an LMS.",
    perks: [
      "Public profile + Verified Trainer wax-seal badge",
      "Featured in /trainer-directory browse",
      "Specialties, certifications, photos",
      "Buyers send inquiries directly",
    ],
    requires_login: true,
  },
  {
    package_id: "pet_parent_pack",
    name: "Pet Parent Pack",
    price: "$19 one-time",
    icon: Heart,
    accent: "crimson",
    persona: "New pet parents",
    desc: "One-shot bundle for someone who just got a puppy and doesn't need a subscription.",
    perks: [
      "1 dog registration ($25 value)",
      "1 Vet Visit PDF unlock",
      "No recurring charges",
    ],
    requires_login: true,
  },
  {
    package_id: "show_resume_pdf",
    name: "Show Resume PDF",
    price: "$12 one-time",
    icon: Scroll,
    accent: "ink",
    persona: "Show & sport handlers",
    desc: "Printable resume for one dog — event history, titles, DNA-verified status, pedigree highlights. Auto-generated from your registry data.",
    perks: [
      "Buyers + judges love this",
      "Auto-generated from event_registrations",
      "DNA-verified status included",
      "Print-ready, share-ready",
    ],
    requires_login: true,
    requires_dog: true,
  },
  {
    package_id: "api_partner_monthly",
    name: "API Partner",
    price: "$499 / mo",
    icon: Code,
    accent: "ink",
    persona: "Kennel-management software vendors",
    desc: "White-label API access for vendors and high-volume operators. 10× rate limits, webhook delivery, partner badge.",
    perks: [
      "10× rate limits on all read endpoints",
      "Webhook delivery for kennel events",
      "Partner badge on /partners",
      "Kennel Elite baseline included",
      "Dedicated integration support",
    ],
    requires_login: true,
  },
];

export default function PricingSpecialty() {
  // App Store 3.1.1 — no pricing/purchase UI inside the native shell.
  if (isNativeApp()) return <NativePlanNotice />;
  return <PricingSpecialtyWeb />;
}

function PricingSpecialtyWeb() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const buy = async (sku) => {
    if (!user) { navigate("/login?next=/pricing/specialty"); return; }
    if (sku.requires_dog) {
      // Show Resume PDF needs a dog. Bounce to the user's first dog
      // or let them pick from their kennel.
      navigate("/kennel?intent=show_resume");
      return;
    }
    setBusy(sku.package_id);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        package_id: sku.package_id,
        success_url: `${webOrigin()}/billing/return?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${webOrigin()}/pricing/specialty`,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  return (
    <Shell>
      <SeoHead
        title="Specialty pricing · Armstrong K9 Registry"
        description="Trainer Profile, Pet Parent Pack, Show Resume PDF, and API Partner — the specialty SKUs for personas the main ladder doesn't fully cover."
        url={`${webOrigin()}/pricing/specialty`}
        type="website"
      />
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="pricing-specialty-page">
        <ReaderAppBanner />
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Specialty pricing
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          For everyone the main ladder doesn't cover.
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Four standalone SKUs designed for narrower personas. Each pairs with
          the rest of the platform; none require a full plan upgrade.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {SPECIALTY_SKUS.map((sku) => {
            const Icon = sku.icon;
            const accent = {
              ink: { bg: "bg-stone-50 border-stone-200", text: "text-ink" },
              gold: { bg: "bg-[#FBF7EC] border-[#C9A227]", text: "text-[#8A6A00]" },
              crimson: { bg: "bg-[#FBF1EE] border-crimson/40", text: "text-crimson" },
            }[sku.accent];
            return (
              <div
                key={sku.package_id}
                className="rounded-2xl border-2 border-stone-200 bg-white p-6 flex flex-col"
                data-testid={`specialty-${sku.package_id}`}
              >
                <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${accent.bg} mb-4`}>
                  <Icon size={20} weight="duotone" className={accent.text} />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] mb-1">
                  {sku.persona}
                </p>
                <h3 className="font-spectral text-2xl tracking-tight">{sku.name}</h3>
                <p className="text-[13px] text-[#6B6B6B] mt-1 leading-relaxed">{sku.desc}</p>
                <div className="my-5">
                  <p className="font-spectral text-3xl text-ink">{sku.price}</p>
                  {sku.annual_price && (
                    <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                      or {sku.annual_price} (save ~18%)
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {sku.perks.map((p, i) => (
                    <li key={i} className="text-[13px] text-ink/80 leading-relaxed flex items-start gap-2">
                      <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full ${accent.text === "text-ink" ? "bg-ink/40" : "bg-current"} ${accent.text}`}/>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => buy(sku)}
                  disabled={busy === sku.package_id}
                  className="w-full rounded-full h-11 bg-ink hover:bg-crimson text-white"
                  data-testid={`specialty-buy-${sku.package_id}`}
                >
                  {busy === sku.package_id ? "Redirecting…" : sku.requires_dog ? "Pick a dog →" : "Buy now"}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[12px] text-[#6B6B6B] mt-10">
          <Link to="/pricing" className="text-crimson underline font-bold">← Back to main pricing</Link>
        </p>
      </div>
    </Shell>
  );
}
